import { useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { exportErrorMessage } from '../exportErrors';
import { CHART_ACCOUNT_TYPES, type ChartAccount, type ChartAccountType, type ChartImportResult } from '../exportTypes';
import { accountingGateway } from '../index';

const NUMBER_MAX = 30;
const NAME_MAX = 150;

/** Add an account (when `account` is absent) or edit an existing account's name and external reference (its number and type never change). */
export function AccountDialog({ account, onClose, onSaved }: { account?: ChartAccount; onClose: () => void; onSaved: (saved: ChartAccount) => void }) {
  const uid = useId();
  const summaryRef = useRef<HTMLDivElement>(null);
  const [number, setNumber] = useState(account?.accountNumber ?? '');
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState<ChartAccountType | ''>(account?.accountType ?? '');
  const [external, setExternal] = useState(account?.externalReference ?? '');
  const [issues, setIssues] = useState<SummaryIssue[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = (key: string) => `${uid}-${key}`;
  const editing = account !== undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    const found: SummaryIssue[] = [];
    if (!editing && !number.trim()) found.push({ key: 'number', message: 'Enter an account number.' });
    if (number.trim().length > NUMBER_MAX) found.push({ key: 'number', message: `Keep the account number to ${NUMBER_MAX} characters or fewer.` });
    if (!name.trim()) found.push({ key: 'name', message: 'Enter an account name.' });
    if (name.trim().length > NAME_MAX) found.push({ key: 'name', message: `Keep the account name to ${NAME_MAX} characters or fewer.` });
    if (!editing && !type) found.push({ key: 'type', message: 'Choose an account type.' });
    setIssues(found);
    if (found.length > 0) {
      queueMicrotask(() => summaryRef.current?.focus());
      return;
    }
    setBusy(true);
    try {
      const saved = editing
        ? await accountingGateway.updateChartAccount(account.id, { name, externalReference: external })
        : await accountingGateway.createChartAccount({ accountNumber: number, name, accountType: type as ChartAccountType, externalReference: external });
      onSaved(saved);
    } catch (err) {
      setServerError(exportErrorMessage(err, editing ? 'The account could not be saved.' : 'The account could not be added.'));
      setBusy(false);
    }
  }

  const has = (key: string) => issues.some((i) => i.key === key);
  const invalid = (key: string) => (has(key) ? { 'aria-invalid': true as const, 'aria-describedby': `${id(key)}-err` } : {});
  const err = (key: string) => issues.filter((i) => i.key === key).map((i) => <p key={i.message} id={`${id(key)}-err`} className="jr-error">{i.message}</p>);

  return (
    <ModalDialog
      title={editing ? `Edit account ${account.accountNumber}` : 'Add account'}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={id('form')} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add account'}</button>
        </>
      )}
    >
      <form id={id('form')} onSubmit={(e) => void submit(e)} noValidate>
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={(key) => document.getElementById(id(key))?.focus()} />
        {serverError && <p className="jr-error jr-error--block" role="alert">{serverError}</p>}
        <div className="jr-grid">
          <div className="jr-field">
            <label htmlFor={id('number')}>Account number {!editing && <span aria-hidden="true" className="jr-req">*</span>}</label>
            <input id={id('number')} value={number} onChange={(e) => setNumber(e.target.value)} readOnly={editing} maxLength={NUMBER_MAX} required={!editing} data-autofocus={!editing} {...invalid('number')} />
            {editing ? <p className="jr-hint">An account number cannot be changed: exports and mappings already refer to it.</p> : <p className="jr-hint">Unique in your chart, ignoring upper and lower case.</p>}
            {err('number')}
          </div>
          <div className="jr-field">
            <label htmlFor={id('type')}>Account type {!editing && <span aria-hidden="true" className="jr-req">*</span>}</label>
            <select id={id('type')} value={type} onChange={(e) => setType(e.target.value as ChartAccountType)} disabled={editing} required={!editing} {...invalid('type')}>
              <option value="">Choose a type</option>
              {CHART_ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            {err('type')}
          </div>
          <div className="jr-field jr-grid__wide">
            <label htmlFor={id('name')}>Account name <span aria-hidden="true" className="jr-req">*</span></label>
            <input id={id('name')} value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} required data-autofocus={editing} {...invalid('name')} />
            {err('name')}
          </div>
          <div className="jr-field jr-grid__wide">
            <label htmlFor={id('external')}>External reference (optional)</label>
            <input id={id('external')} value={external} onChange={(e) => setExternal(e.target.value)} maxLength={100} aria-describedby={id('external-hint')} />
            <p id={id('external-hint')} className="jr-hint">A placeholder for an identifier in another system. EPN does not connect to one in this release.</p>
          </div>
        </div>
      </form>
    </ModalDialog>
  );
}

const HEADER = 'accountNumber,name,accountType';

/**
 * Import accounts from CSV. The file is checked first (a dry run that saves nothing and lists any row errors), and Import is only offered once a check
 * has passed for exactly the text shown. The import is all-or-nothing: if any row is refused, nothing is saved.
 */
export function ImportDialog({ onClose, onImported }: { onClose: () => void; onImported: (accepted: number) => void }) {
  const uid = useId();
  const [csv, setCsv] = useState('');
  const [checked, setChecked] = useState<{ csv: string; result: ChartImportResult } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'check' | 'import' | null>(null);
  const id = (key: string) => `${uid}-${key}`;
  const passed = checked !== null && checked.csv === csv && checked.result.rejected === 0 && checked.result.accepted > 0;

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (file.size > 1024 * 1024) {
      setError('That file is larger than 1 MB. Split it into smaller files.');
      return;
    }
    setCsv(await file.text());
    setChecked(null);
  }

  async function run(dryRun: boolean) {
    setError(null);
    if (!csv.trim()) {
      setError('Paste the CSV text or choose a file first.');
      return;
    }
    setBusy(dryRun ? 'check' : 'import');
    try {
      const result = await accountingGateway.importChartAccounts(csv, dryRun);
      if (dryRun) setChecked({ csv, result });
      else if (result.rejected > 0) setChecked({ csv, result });
      else onImported(result.accepted);
    } catch (err) {
      setError(exportErrorMessage(err, 'The file could not be checked.'));
    } finally {
      setBusy(null);
    }
  }

  const result = checked?.result ?? null;
  return (
    <ModalDialog
      title="Import chart of accounts"
      size="lg"
      busy={busy !== null}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy !== null}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={() => void run(true)} disabled={busy !== null}>{busy === 'check' ? 'Checking…' : 'Check file (dry run)'}</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void run(false)} disabled={!passed || busy !== null}>
            {busy === 'import' ? 'Importing…' : result && passed ? `Import ${result.accepted} account${result.accepted === 1 ? '' : 's'}` : 'Import accounts'}
          </button>
        </>
      )}
    >
      <p className="jr-hint">
        The first row must be the header <code>{HEADER}</code>. Account type is one of Asset, Liability, Equity, Revenue, Expense or Clearing.
        A file can hold up to 5,000 accounts and 1 MB. Duplicate account numbers are refused, and if any row is refused nothing is imported.
      </p>
      <div className="jr-field">
        <label htmlFor={id('file')}>Choose a CSV file</label>
        <input id={id('file')} type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e)} data-autofocus />
      </div>
      <div className="jr-field">
        <label htmlFor={id('csv')}>Or paste the CSV text</label>
        <textarea id={id('csv')} className="ax-textarea" rows={7} value={csv} onChange={(e) => { setCsv(e.target.value); setChecked(null); }} spellCheck={false} placeholder={`${HEADER}\n7000,Marketing,Expense`} />
      </div>
      {error && <p className="jr-error jr-error--block" role="alert">{error}</p>}
      {result && (
        <div role="status" className="ax-import-result">
          {result.rejected === 0 ? (
            <p className="jr-notice jr-notice--ok"><span aria-hidden="true">✔ </span>The file passed the check: {result.accepted} account{result.accepted === 1 ? '' : 's'} can be imported. Nothing has been saved yet.</p>
          ) : (
            <p className="jr-error jr-error--block" role="alert">
              <span aria-hidden="true">✖ </span>{result.rejected} row{result.rejected === 1 ? ' was' : 's were'} refused, so nothing {result.dryRun ? 'would be' : 'was'} imported. Fix the rows below and check again.
            </p>
          )}
          {result.errors.length > 0 && (
            <div className="jr-table-scroll" role="region" aria-label="Rows that need fixing" tabIndex={0}>
              <table className="jr-table" data-testid="import-errors">
                <caption className="ax-caption">Rows that need fixing (row 1 is the header)</caption>
                <thead><tr><th scope="col" className="num">Row</th><th scope="col">Problem</th></tr></thead>
                <tbody>{result.errors.map((e, i) => <tr key={`${e.row}-${i}`}><td className="num">{e.row}</td><td>{e.message}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ModalDialog>
  );
}
